import { useRef, useState, type CSSProperties } from 'react';
import { Pause, Play, Volume2, VolumeX } from 'lucide-react';
import './recording-player.css';

interface RecordingPlayerProps {
  src: string;
  label: string;
  durationSeconds?: number;
}

function formatDuration(seconds: number): string {
  const safe = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const minutes = Math.floor(safe / 60);
  return `${minutes}:${String(safe % 60).padStart(2, '0')}`;
}

export function RecordingPlayer({ src, label, durationSeconds = 0 }: RecordingPlayerProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [mediaDuration, setMediaDuration] = useState(0);
  const pendingSeekRef = useRef<number | null>(null);
  const lastAudibleVolumeRef = useRef(1);
  const totalDuration = mediaDuration > 0 ? mediaDuration : Math.max(0, durationSeconds);
  const progress = totalDuration > 0 ? Math.min(100, (currentTime / totalDuration) * 100) : 0;

  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch {
      setPlaying(false);
    }
  }

  function seek(value: number) {
    const next = Math.min(Math.max(0, value), totalDuration || value);
    setCurrentTime(next);
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.readyState === 0) {
      const shouldLoad = pendingSeekRef.current === null;
      pendingSeekRef.current = next;
      if (shouldLoad) audio.load();
      return;
    }
    audio.currentTime = next;
  }

  function toggleMuted() {
    const nextMuted = !muted;
    let nextVolume = volume;
    if (!nextMuted && nextVolume === 0) {
      nextVolume = lastAudibleVolumeRef.current;
      setVolume(nextVolume);
    }
    setMuted(nextMuted);
    if (audioRef.current) {
      audioRef.current.volume = nextVolume;
      audioRef.current.muted = nextMuted;
    }
  }

  function changeVolume(percent: number) {
    const nextVolume = Math.min(1, Math.max(0, percent / 100));
    const nextMuted = nextVolume === 0;
    if (nextVolume > 0) lastAudibleVolumeRef.current = nextVolume;
    setVolume(nextVolume);
    setMuted(nextMuted);
    if (audioRef.current) {
      audioRef.current.volume = nextVolume;
      audioRef.current.muted = nextMuted;
    }
  }

  return <div className="telephony-recording-player">
    <audio
      ref={audioRef}
      className="telephony-recording-player__media"
      aria-label={label}
      preload="none"
      src={src}
      onLoadedMetadata={event => {
        const nextDuration = event.currentTarget.duration;
        if (Number.isFinite(nextDuration) && nextDuration > 0) setMediaDuration(nextDuration);
        if (pendingSeekRef.current !== null) {
          event.currentTarget.currentTime = pendingSeekRef.current;
          pendingSeekRef.current = null;
        }
      }}
      onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime)}
      onPlay={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      onEnded={() => {
        setPlaying(false);
        setCurrentTime(0);
      }}
    />
    <button
      type="button"
      className="telephony-recording-player__button"
      onClick={togglePlayback}
      aria-label={playing ? 'Пауза' : 'Воспроизвести запись'}
      title={playing ? 'Пауза' : 'Воспроизвести'}
    >{playing ? <Pause /> : <Play />}</button>
    <div className="telephony-recording-player__timeline">
      <input
        type="range"
        min="0"
        max={Math.max(totalDuration, 0.1)}
        step="0.1"
        value={Math.min(currentTime, Math.max(totalDuration, 0.1))}
        onChange={event => seek(Number(event.target.value))}
        aria-label="Перемотка записи"
        aria-valuetext={`${formatDuration(currentTime)} из ${formatDuration(totalDuration)}`}
        title={`${formatDuration(currentTime)} из ${formatDuration(totalDuration)}`}
        style={{ '--recording-progress': `${progress}%` } as CSSProperties}
      />
      <output aria-live="off">{formatDuration(currentTime)} / {formatDuration(totalDuration)}</output>
    </div>
    <div className="telephony-recording-player__volume-control">
      <button
        type="button"
        className="telephony-recording-player__button telephony-recording-player__volume"
        onClick={toggleMuted}
        aria-label={muted ? 'Включить звук записи' : 'Отключить звук записи'}
        title={muted ? 'Включить звук' : 'Отключить звук'}
      >{muted ? <VolumeX /> : <Volume2 />}</button>
      <span className="telephony-recording-player__volume-popover">
        <input
          className="telephony-recording-player__volume-slider"
          type="range"
          min="0"
          max="100"
          step="1"
          value={Math.round(volume * 100)}
          onChange={event => changeVolume(Number(event.target.value))}
          aria-label="Громкость записи"
          aria-valuetext={`${Math.round(volume * 100)}%`}
          title={`Громкость: ${Math.round(volume * 100)}%`}
        />
      </span>
    </div>
  </div>;
}
